import { PageData } from 'actions/pageActions'
import { CookieProps } from 'components/SetClientCookie/SetClientCookie'
import { SearchParams } from 'utils/headerManager/headerManager'
import { ExperimentVariation } from './ProviderData'
import { PageProviderClient } from './PageProviderClient'
import {
  getFeatureDisabledElements,
  getFeatureDisabledRoutes,
  getRoutesForProgressPage,
} from 'utils/next/routes'
import { getPageMetaTitleForRoute } from 'utils/metadata/metaTags'
import { mainProductConfig } from 'constants/mainProductConfig'
import { Alchemer } from 'components/Alchemer'
import { FEATURE_TOGGLES } from 'constants/featureToggles'
import Script from 'next/script'
import CustomJsonLd from 'components/Schemas/CustomJsonLd'
import { _get } from 'utils/shared'
import SITE_ENVIRONMENTS from 'constants/websites'
import { AssociatedProductData } from '@/app/contentful/contenfulTypes'
import { DTIProviderServer } from 'providers/DTIProvider/DTIProviderServer'
import { buildInitialDtiData } from 'providers/DTIProvider/buildInitialDtiData'
import { ServerProviders } from './ServerProviders'
import { PageLayoutClient } from './PageLayoutClient'
import { resolveLayoutSiteEnv } from 'utils/resolveLayoutSiteEnv'
import JsonLdBlocks from 'components/Schemas/JsonLdBlocks'
import { hasJsonLdType } from 'lib/jsonLd'

type FooterJsonConfig = {
  schema?: {
    applicationCategory?: string
    applicationSubCategory?: string
    reviewCount?: string
    ratingValue?: string
    offersType?: string
    offerPrice?: string
    offerPriceCurrency?: string
  }
}

type PageProvider = {
  children: React.ReactNode
  pageProps: PageData & {
    pageVariation?: ExperimentVariation | null
    pageSectionsVariations?: ExperimentVariation[]
    quiz_name?: string
    quiz_page?: string
  }
}

export type PageProviderServer = {
  children: React.ReactNode
  pageProps: SlimmedProps
}

export type SlimmedProps = {
  featureToggleObject: {
    [key: string]: string
  }
  featurePathOverrides?: Record<string, string> | null
  content_type: string
  associatedProductData?: AssociatedProductData
  computedPhoneData?: {
    phoneNumber?: string
    phoneCopy?: string
  }
  predefinedChatPrompts: string[]
  publicProcessDotEnv: Record<string, string>
  specificHeaderCtaDataFiltered: string | unknown
  associatedProduct: string
  query?: SearchParams
  fullAsPath?: string
  protocol?: string
  pageHost?: string
  cookiesToSet: CookieProps[]
  pageMetaTitle: string
  pageTitle?: string
  metaDescription?: string
  canonicalLinkOverride?: string | null
  canonicalUrlDefault: string
  pageDisclosure?: string
  secondaryDisclosures: []
  quiz_name: string | undefined
  quiz_page: string | undefined
  pageVariation: ExperimentVariation | null
  pageSectionsVariations: ExperimentVariation[]
  graduate?: Record<string, unknown> | null
  largestDebtAmount?: number
  fastestCompletion?: number
  isMobileUA: boolean
  header: Record<string, unknown> | null
  footer: Record<string, unknown> | null
  pageConfig?: unknown | null
  tcbBannerLayout?: boolean
  alignContentBottom?: boolean
  achieveConsentBanner?: Record<string, unknown>[]
  showPhoneNumber?: boolean
  siteInfo?: Record<string, unknown>
  footerSticky?: Record<string, unknown> | null
  dynamicStickyNav?: Record<string, unknown> | null
  cookies: Record<string, string>
  disabledRoutes: string[]
  disabledElements: Record<string, unknown>
  showProgressPage: boolean
  customPhoneData?: Record<string, unknown>
  dynamicPageMetaTitle: string
  titlePage: string
  webpageSchema: Record<string, unknown>
  initialDtiData?: unknown
}

export function PageProvider({ children, pageProps }: PageProvider) {
  if (pageProps?.notFound) {
    return
  }

  const siteEnv = pageProps?.publicProcessDotEnv?.NEXT_PUBLIC_SITE_ENVIRONMENT

  const canonicalUrlDefault = `${pageProps?.protocol}://${pageProps?.pageHost}${
    pageProps?.fullAsPath === '/' ? '' : pageProps?.fullAsPath
  }${Number(pageProps?.query?.page) > 1 ? '?page=' + pageProps?.query?.page : ''}`

  const pageMetaTitle = `${pageProps.content?.fields?.pageMetaTitle}${
    Number(pageProps?.query?.page) > 1 ? ' page ' + pageProps?.query?.page : ''
  }`

  // Business Logic: Default page product to Personal Loan
  const associatedProduct = pageProps?.content?.fields?.associatedProduct ?? 'Personal Loans'

  let specificHeaderCtaDataFiltered

  if (
    // Enable product specific CTA phone numbers for Achieve website only
    siteEnv === SITE_ENVIRONMENTS.ACHIEVE &&
    // Do not enable on landing pages (/l/) or article pages (/a/)
    !['l', 'a', 'lp', 'achieve-pro'].includes(pageProps?.fullAsPath?.split('/')?.[1])
  ) {
    specificHeaderCtaDataFiltered = pageProps?.specificHeaderCtaData?.[associatedProduct]
  }

  // Use pre-computed experiment data from page component to avoid duplicate calls
  // These values are now passed in from the page component where getExperimentData is called
  const quiz_name = pageProps?.quiz_name
  const quiz_page = pageProps?.quiz_page
  const pageVariation = pageProps?.pageVariation ?? null
  const pageSectionsVariations = pageProps?.pageSectionsVariations ?? []

  const dynamicPathname = pageProps?.fullAsPath?.split('?')[0] || '/'

  const disabledRoutes = getFeatureDisabledRoutes(pageProps?.featureToggleObject, siteEnv)
  const disabledElements = getFeatureDisabledElements(pageProps?.featureToggleObject, siteEnv)
  const showProgressPage = getRoutesForProgressPage.includes(dynamicPathname)

  let customPhoneData
  const pageConfig = pageProps?.content?.fields?.pageConfig as
    | { customPhoneData?: Record<string, unknown> }
    | undefined
  if (pageConfig?.customPhoneData) {
    customPhoneData = {
      utm_source: pageProps?.query?.utm_source ?? null,
      utm_content: pageProps?.query?.utm_content ?? null,
      ...pageConfig.customPhoneData,
    }
  }

  const dynamicPageMetaTitle = getPageMetaTitleForRoute(
    dynamicPathname,
    pageMetaTitle,
    pageProps?.content?.fields?.pageTitle
  )
  const defaultPageMetaTitle =
    mainProductConfig[(siteEnv as keyof typeof mainProductConfig) || 'achieve']?.['MainName']
  const titlePage = dynamicPageMetaTitle
    ? `${dynamicPageMetaTitle} ${dynamicPathname === '/' ? '' : `| ${defaultPageMetaTitle}`}`
    : defaultPageMetaTitle

  const canonicalLink = pageProps?.content?.fields?.canonicalLinkOverride || canonicalUrlDefault
  const webpageSchema: Record<string, unknown> = {
    '@context': 'https://schema.org/',
    '@type': 'WebPage',
    name: titlePage,
    url: `${canonicalLink}/`,
    description: pageProps?.content?.fields?.metaDescription,
    inLanguage: 'en-US',
  }
  const cmsPageSchemas = pageProps?.cmsPageSchemas ?? null

  const predefinedChatPrompts =
    pageProps?.associatedProductData?.predefinedChatPrompts?.[associatedProduct] || []

  // Compute phone data on the server
  let computedPhoneData
  if (pageProps?.associatedProductData) {
    computedPhoneData = {
      phoneNumber: pageProps.associatedProductData?.phoneNumbers?.[associatedProduct],
      phoneCopy: pageProps.associatedProductData?.phoneCopy?.[associatedProduct],
    }
  }

  if (pageProps?.content?.fields?.phoneOverride) {
    computedPhoneData = {
      phoneNumber: pageProps.content.fields.phoneOverride,
    }
  }

  const resolvedLayoutSiteEnv = resolveLayoutSiteEnv({
    header: pageProps?.header,
    siteInfo: pageProps?.siteInfo,
    publicProcessDotEnv: pageProps?.publicProcessDotEnv,
  })

  const slimmedProps: SlimmedProps = {
    header:
      pageProps?.header != null
        ? { ...pageProps.header, siteEnv: resolvedLayoutSiteEnv }
        : { siteEnv: resolvedLayoutSiteEnv },
    footer: pageProps?.footer,
    featureToggleObject: pageProps?.featureToggleObject,
    featurePathOverrides: pageProps?.featurePathOverrides,
    content_type: pageProps?.content?.items?.[0]?.fields.contentType,
    predefinedChatPrompts: predefinedChatPrompts,
    publicProcessDotEnv: pageProps?.publicProcessDotEnv,
    computedPhoneData,
    specificHeaderCtaDataFiltered,
    associatedProduct,
    pageTitle: pageProps?.content?.fields?.pageTitle,
    pageMetaTitle,
    metaDescription: pageProps?.content?.fields?.metaDescription,
    canonicalLinkOverride: pageProps?.content?.fields?.canonicalLinkOverride,
    canonicalUrlDefault,
    pageDisclosure: pageProps?.content?.fields?.pageDisclosure,
    secondaryDisclosures: pageProps?.content?.fields?.secondaryDisclosures,
    quiz_name,
    quiz_page,
    pageVariation,
    pageSectionsVariations,
    graduate: pageProps?.graduate,
    largestDebtAmount: pageProps?.largestDebtAmount,
    fastestCompletion: pageProps?.fastestCompletion,
    cookiesToSet: pageProps?.cookiesToSet,
    isMobileUA: pageProps?.isMobile,
    pageConfig: pageProps?.content?.fields?.pageConfig,
    tcbBannerLayout: pageProps?.tcbBannerLayout,
    alignContentBottom: pageProps?.tcbBannerLayout,
    achieveConsentBanner: pageProps?.achieveConsentBanner,
    showPhoneNumber: pageProps?.content?.fields?.showPhoneNumber,
    siteInfo: pageProps?.siteInfo,
    footerSticky: pageProps?.footerSticky,
    dynamicStickyNav: pageProps?.dynamicStickyNav,
    cookies: pageProps?.cookies,
    disabledRoutes,
    disabledElements: disabledElements as Record<string, unknown>,
    showProgressPage,
    customPhoneData,
    dynamicPageMetaTitle,
    titlePage,
    webpageSchema,
    initialDtiData: buildInitialDtiData(pageProps),
  }

  const header = slimmedProps.header

  //footer Logic
  const isFDRPage = pageProps.siteInfo?.url === 'https://www.freedomdebtrelief.com/'
  const printWebAppSchema = !isFDRPage
  const printLocalBusinessSchema = isFDRPage
  const jsonConfigFooter = _get(pageProps?.footer, 'config') as FooterJsonConfig | undefined
  const brandCopy = _get(pageProps?.footer, 'logoHelpText') || pageProps?.siteInfo?.footerBrandCopy
  const footerSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    applicationCategory: jsonConfigFooter?.schema?.applicationCategory || '',
    applicationSubCategory: jsonConfigFooter?.schema?.applicationSubCategory || '',
    name: pageProps?.siteInfo?.mainName,
    aggregateRating: {
      reviewCount: [Number(jsonConfigFooter?.schema?.reviewCount?.replace(/,/g, '')) || 0],
      ratingValue: [Number(jsonConfigFooter?.schema?.ratingValue?.replace(/,/g, '')) || 0],
      bestRating: 5,
    },
    offers: {
      '@type': jsonConfigFooter?.schema?.offersType,
      price: jsonConfigFooter?.schema?.offerPrice,
      priceCurrency: jsonConfigFooter?.schema?.offerPriceCurrency,
    },
    operatingSystem: 'All',
    copyrightYear: new Date().getFullYear(),
    description: brandCopy,
  }

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: pageProps?.siteInfo?.mainName,
    url: pageProps?.siteInfo?.publicUrl,
    logo: pageProps?.siteInfo?.logoUrl,
    hasMap: 'https://www.google.com/maps?cid=1138888979161863326',
    description: pageProps?.siteInfo?.description,
    address: {
      '@type': 'PostalAddress',
      streetAddress: pageProps?.siteInfo?.streetAddress,
      addressLocality: pageProps?.siteInfo?.addressLocality,
      addressRegion: pageProps?.siteInfo?.addressRegion,
      postalCode: pageProps?.siteInfo?.postalCode,
      addressCountry: { '@type': 'Country', name: 'US' },
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: pageProps?.siteInfo?.telephone || '',
      contactType: 'customer service',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      reviewCount: [Number(jsonConfigFooter?.schema?.reviewCount?.replace(/,/g, '')) || 0],
      ratingValue: [Number(jsonConfigFooter?.schema?.ratingValue?.replace(/,/g, '')) || 0],
    },
    potentialAction: {
      '@type': 'WriteAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: pageProps?.siteInfo?.publicUrl + '/reviews/write',
        inLanguage: 'en',
        actionPlatform: [
          'http://schema.org/DesktopWebPlatform',
          'http://schema.org/MobileWebPlatform',
        ],
      },
    },
    parentOrganization: {
      '@type': 'Organization',
      name: pageProps?.siteInfo?.mainName,
      legalName: pageProps?.siteInfo?.mainName,
      logo: {
        '@type': 'ImageObject',
        url: pageProps?.siteInfo?.logoUrl,
      },
      sameAs: pageProps?.siteInfo?.socialMediaLinks,
    },
  }

  const footerOrganizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: pageProps?.siteInfo?.mainName,
    alternateName: pageProps?.siteInfo?.url,
    url: pageProps?.siteInfo?.publicUrl,
    logo: { '@type': 'ImageObject', url: pageProps?.siteInfo?.logoUrl },
    image: pageProps?.siteInfo?.image,
    description: pageProps?.siteInfo?.description,
    sameAs: pageProps?.siteInfo?.socialMediaLinks,
    address: {
      '@type': 'PostalAddress',
      streetAddress: pageProps?.siteInfo?.streetAddress,
      addressLocality: pageProps?.siteInfo?.addressLocality,
      addressRegion: pageProps?.siteInfo?.addressRegion,
      postalCode: pageProps?.siteInfo?.postalCode,
      addressCountry: { '@type': 'Country', name: 'US' },
    },
    telephone: pageProps?.siteInfo?.telephone || '',
    contactPoint: [
      {
        '@type': 'ContactPoint',
        type: 'ContactPoint',
        telephone: pageProps?.siteInfo?.postalCode,
        contactType: 'customer service',
        areaServed: ['US'],
        availableLanguage: pageProps?.siteInfo?.availableLanguage,
      },
    ],
  }

  return (
    <div
      className="flex flex-col items-stretch min-h-screen justify-between"
      page-main-variation={header?.siteEnv}
    >
      <PageProviderClient pageProps={slimmedProps}>
        <ServerProviders
          featureToggleObject={slimmedProps.featureToggleObject}
          featurePathOverrides={slimmedProps.featurePathOverrides}
          graduate={slimmedProps.graduate ?? null}
          largestDebtAmount={slimmedProps.largestDebtAmount}
          fastestCompletion={slimmedProps.fastestCompletion}
        >
          <PageLayoutClient pageProps={slimmedProps}>
            <DTIProviderServer initialDataDti={slimmedProps.initialDtiData}>
              {children}
            </DTIProviderServer>
          </PageLayoutClient>
        </ServerProviders>
      </PageProviderClient>

      {!!(
        header?.siteEnv &&
        (disabledElements as Record<string, unknown>)?.[
          (
            FEATURE_TOGGLES[header?.siteEnv as keyof typeof FEATURE_TOGGLES] as
              | Record<string, string>
              | undefined
          )?.ACX_WEB_ENABLE_ALCHEMER_SURVEY as string
        ]
      ) && (
        // Alchemer survey script
        <Alchemer />
      )}
      <JsonLdBlocks schemas={cmsPageSchemas} />
      {!hasJsonLdType(cmsPageSchemas, 'WebPage') && webpageSchema && (
        <CustomJsonLd id="webPage" data={webpageSchema} />
      )}
      {typeof slimmedProps?.siteInfo?.disclosureScriptUrl === 'string' && (
        <Script src={slimmedProps.siteInfo.disclosureScriptUrl} strategy="afterInteractive" />
      )}
      {printWebAppSchema && (
        <>
          {!hasJsonLdType(cmsPageSchemas, 'WebApplication') && (
            <CustomJsonLd id="review" data={footerSchema} />
          )}
          {!hasJsonLdType(cmsPageSchemas, 'Organization') && (
            <CustomJsonLd id="organization" data={footerOrganizationSchema} />
          )}
        </>
      )}
      {printLocalBusinessSchema && !hasJsonLdType(cmsPageSchemas, 'LocalBusiness') && (
        <CustomJsonLd id="localBusiness" data={localBusinessSchema} />
      )}
    </div>
  )
}
